import { metresToDistance, distanceToMetres, distanceUnitLabel } from './DistanceUtil';

const SECONDS_IN_HOUR = 3600;
const SECONDS_IN_MINUTE = 60;
const METRES_TO_FEET = 3.28084;

// A plan cell's value belongs to a specific Goal column, and is stored raw
// the same way the goal itself is (metres for DISTANCE/ELEVATION_GAIN,
// seconds for TIME, a plain count for ACTIVITY_COUNT). Conversion to/from
// what the user types happens here, driven by the column's own unit plus
// the live distance_unit/elevation_unit settings — mirroring GoalEditor's
// conversion. `options.timeInMinutes` switches a TIME value's input/label
// unit from decimal hours to whole minutes — used for day-level cells,
// where a session is more naturally typed as "45" than "0.75".
export const goalValueToDisplay = (storedValue, goalUnit, unitSettings, options = {}) => {
    if (storedValue === null || storedValue === undefined) return null;
    if (goalUnit === 'DISTANCE') return metresToDistance(storedValue, unitSettings.distance_unit);
    if (goalUnit === 'TIME') return storedValue / (options.timeInMinutes ? SECONDS_IN_MINUTE : SECONDS_IN_HOUR);
    if (goalUnit === 'ELEVATION_GAIN') return unitSettings.elevation_unit === 'FEET' ? storedValue * METRES_TO_FEET : storedValue;
    return storedValue; // ACTIVITY_COUNT
};

export const goalValueToStored = (displayValue, goalUnit, unitSettings, options = {}) => {
    if (displayValue === null || displayValue === undefined || displayValue === '') return null;
    const numericValue = Number(displayValue);
    if (!Number.isFinite(numericValue)) return null;
    if (goalUnit === 'DISTANCE') return distanceToMetres(numericValue, unitSettings.distance_unit);
    if (goalUnit === 'TIME') return numericValue * (options.timeInMinutes ? SECONDS_IN_MINUTE : SECONDS_IN_HOUR);
    if (goalUnit === 'ELEVATION_GAIN') return unitSettings.elevation_unit === 'FEET' ? numericValue / METRES_TO_FEET : numericValue;
    return numericValue; // ACTIVITY_COUNT
};

export const goalUnitLabel = (goalUnit, unitSettings, options = {}) => {
    if (goalUnit === 'DISTANCE') return distanceUnitLabel(unitSettings.distance_unit);
    if (goalUnit === 'TIME') return options.timeInMinutes ? 'mins' : 'hrs';
    if (goalUnit === 'ELEVATION_GAIN') return unitSettings.elevation_unit === 'FEET' ? 'ft' : 'm';
    return 'activities';
};

// e.g. 12 -> "12", 12.10 -> "12.1", 12.34 -> "12.34" — no forced trailing zeros.
const formatTrimmed = (value, precision) => parseFloat(value.toFixed(precision)).toString();

// e.g. 5400 (seconds) -> "1h 30m", 3600 -> "1h", 900 -> "15m". A goal's
// planned/actual/total Time reads more naturally broken into hours and
// minutes than as a bare decimal-hours number like "1.5".
const formatHrsMins = (storedSeconds) => {
    const totalMinutes = Math.round(storedSeconds / SECONDS_IN_MINUTE);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (hrs === 0) return `${mins}m`;
    if (mins === 0) return `${hrs}h`;
    return `${hrs}h ${mins}m`;
};

// `options.timeInMinutes` renders TIME as a plain "45 mins" instead of the
// usual hrs/mins breakdown — used for day-level cells, where the hrs/mins
// split is more clutter than clarity for a single short session.
export const formatGoalValue = (storedValue, goalUnit, unitSettings, options = {}) => {
    if (storedValue === null || storedValue === undefined) return '—';
    if (goalUnit === 'TIME' && !options.timeInMinutes) return formatHrsMins(storedValue);
    const displayValue = goalValueToDisplay(storedValue, goalUnit, unitSettings, options);
    const precision = goalUnit === 'ACTIVITY_COUNT' || (goalUnit === 'TIME' && options.timeInMinutes) ? 0 : 2;
    return `${formatTrimmed(displayValue, precision)} ${goalUnitLabel(goalUnit, unitSettings, options)}`;
};

// The year total row needs Plan, Actual, and Target to each read clearly on
// their own — a single combined "actual / target" string buried whether a
// number was planned or actually happened. Returns pre-formatted strings for
// each, plus a percent-of-target (null when there's no target to compare to).
// Totals are always a week-or-more of Time, so they always use the hrs/mins
// breakdown, never the day-level minutes-only format.
export const summarizeGoalColumnTotals = (plannedValues, actualValues, targetValue, goalUnit, unitSettings) => {
    const format = (rawTotal) => formatGoalValue(rawTotal, goalUnit, unitSettings);

    const hasAnyPlannedValue = plannedValues.some((value) => value !== null && value !== undefined);
    const plannedTotal = plannedValues.reduce((sum, value) => sum + (value || 0), 0);
    const actualTotal = actualValues.reduce((sum, value) => sum + (value || 0), 0);

    return {
        plan: hasAnyPlannedValue ? format(plannedTotal) : '—',
        actual: format(actualTotal),
        target: targetValue ? format(targetValue) : '—',
        targetPercent: targetValue ? `${Math.round((actualTotal / targetValue) * 100)}%` : null,
    };
};

// e.g. "WEIGHTTRAINING" -> "Weight training", "RUN" -> "Run".
export const formatActivityType = (value) => value === 'WEIGHTTRAINING'
    ? 'Weight training'
    : value.charAt(0) + value.slice(1).toLowerCase();

// A column with exactly one activity type is labelled by that type (e.g.
// "Run") since that's what it concretely represents; a column spanning
// several activity types falls back to the name(s) of the goal(s) behind it.
export const columnLabel = (column) => column.activityTypes.length === 1
    ? formatActivityType(column.activityTypes[0])
    : column.goalNames.join(' / ');
